import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { GetTeamsParams, TeamStatuses } from 'store/teams/types';
import { DeclarationCountry } from 'store/declarations/enums/common/declaration-country';
import { capitalize } from 'lodash';
import useDebouncedValue from 'hooks/useDebouncedValue';

export enum TeamsParamsKeys {
    SORT_DIR = 'sortDirection',
    PAGE = 'page',
    PAGE_SIZE = 'pageSize',
    QUERY = 'query',
    SORT_PARAMETER = 'teamSortParameter',
    MARKET = 'market',
    STATUS = 'status',
}

export enum TeamsSortParams {
    NAME = 'NAME',
    MARKET = 'MARKET',
    CREATED_AT = 'CREATED_AT',
    UPDATED_AT = 'UPDATED_AT',
    CUSTOMERS_COUNT = 'CUSTOMERS_COUNT',
    MEMBERS_COUNT = 'MEMBERS_COUNT',
    STATUS = 'STATUS',
}

export type FilterTags = {
    key: TeamsParamsKeys;
    label: string;
    value: string;
};

const useTeamsFilters = () => {
    const [searchParams, setSearchParams] = useSearchParams();

    const initialQuery = searchParams.get(TeamsParamsKeys.QUERY) ?? '';
    const initialSortDir = (searchParams.get(TeamsParamsKeys.SORT_DIR) as 'ASC' | 'DESC') || 'DESC';
    const initialSort =
        (searchParams.get(TeamsParamsKeys.SORT_PARAMETER) as TeamsSortParams) || TeamsSortParams.CREATED_AT;
    const initialPage = Number(searchParams.get(TeamsParamsKeys.PAGE)) || 0;
    const initialSize = Number(searchParams.get(TeamsParamsKeys.PAGE_SIZE)) || 10;
    const initialStatus = (searchParams.get(TeamsParamsKeys.STATUS) as TeamStatuses) || undefined;

    const [queryInputVal, setQueryInputVal] = useState(initialQuery);
    const [market, setMarket] = useState<DeclarationCountry | undefined>(undefined);
    const [status, setStatus] = useState<TeamStatuses | undefined>(initialStatus);
    const [sortState, setSortState] = useState<{
        teamSortParameter: TeamsSortParams | undefined;
        sortDirection: 'ASC' | 'DESC' | undefined;
    }>({ teamSortParameter: initialSort, sortDirection: initialSortDir });
    const [page, setPage] = useState<number>(initialPage);
    const [size, setSize] = useState<number>(initialSize);

    const debouncedQuery = useDebouncedValue(queryInputVal, 300);

    const writeParams = useCallback(
        (next: Partial<GetTeamsParams>) => {
            const searchParam = new URLSearchParams(searchParams.toString());

            const apply = (key: string, val?: string | null) => {
                if (val === undefined || val === null || val === '') {
                    searchParam.delete(key);
                } else {
                    searchParam.set(key, String(val));
                }
            };

            apply(TeamsParamsKeys.QUERY, next.query);
            apply(TeamsParamsKeys.SORT_PARAMETER, next.teamSortParameter);
            apply(TeamsParamsKeys.SORT_DIR, next.sortDirection);
            apply(TeamsParamsKeys.PAGE, next?.page?.toString());
            apply(TeamsParamsKeys.PAGE_SIZE, next?.size?.toString());
            apply(TeamsParamsKeys.MARKET, next.market);
            apply(TeamsParamsKeys.STATUS, next.status);

            setSearchParams(searchParam, { replace: true });
        },
        [searchParams, setSearchParams]
    );

    useEffect(() => {
        writeParams({
            query: debouncedQuery,
            market,
            status,
            teamSortParameter: sortState.teamSortParameter,
            sortDirection: sortState.sortDirection,
            page,
            size,
        });
    }, [debouncedQuery, writeParams, sortState.teamSortParameter, sortState.sortDirection, page, size, market, status]);

    const appliedParams = useMemo(
        () => ({
            query: debouncedQuery || undefined,
            market,
            status,
            teamSortParameter: sortState.teamSortParameter,
            sortDirection: sortState.sortDirection,
            page,
            size,
        }),
        [debouncedQuery, market, status, sortState.teamSortParameter, sortState.sortDirection, page, size]
    );

    const antdPagination = useMemo(
        () => ({
            current: page + 1,
            pageSize: size,
        }),
        [page, size]
    );

    const tags: FilterTags[] = useMemo(() => {
        const list: FilterTags[] = [];

        if (market) {
            list.push({
                key: TeamsParamsKeys.MARKET,
                label: 'Market',
                value: capitalize(market),
            });
        }

        if (status) {
            list.push({
                key: TeamsParamsKeys.STATUS,
                label: 'Status',
                value: capitalize(status),
            });
        }

        return list;
    }, [market, status]);

    const clearFilter = useCallback((key: string) => {
        switch (key) {
            case TeamsParamsKeys.QUERY: {
                setQueryInputVal('');
                break;
            }

            case TeamsParamsKeys.PAGE: {
                setPage(0);
                break;
            }

            case TeamsParamsKeys.STATUS: {
                setStatus(undefined);
                break;
            }

            case TeamsParamsKeys.MARKET: {
                setMarket(undefined);
                break;
            }

            default: {
                break;
            }
        }
    }, []);

    const clearAll = useCallback(() => {
        setPage(0);
        setQueryInputVal('');
        setMarket(undefined);
        setStatus(undefined);
    }, []);

    return {
        queryInputVal,
        appliedParams,
        debouncedQuery,
        sortState,
        page,
        size,
        antdPagination,
        market,
        status,
        tags,
        setStatus,
        setPage,
        setSize,
        setQueryInputVal,
        setSortState,
        setMarket,
        clearFilter,
        clearAll,
    };
};

export default useTeamsFilters;
